import { motion } from "framer-motion";
import { Compass, ClipboardList, Palette, Rocket, LineChart } from "lucide-react";

const steps = [
  { num: "01", icon: Compass, title: "Discover", desc: "Stakeholder interviews, audience research, and a full audit of your current site, SEO, and funnels." },
  { num: "02", icon: ClipboardList, title: "Plan", desc: "Strategy doc: positioning, sitemap, content plan, SEO targets, KPIs, and a delivery roadmap." },
  { num: "03", icon: Palette, title: "Design", desc: "High-fidelity design and copy, built around conversion — reviewed and refined with you weekly." },
  { num: "04", icon: Rocket, title: "Launch", desc: "Blazing-fast build, SEO-safe migration, analytics, and a proper go-live checklist." },
  { num: "05", icon: LineChart, title: "Optimize", desc: "Monthly experiments on content, UX, and paid — compounding growth after launch." },
];

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="section-pad relative">
      <div className="container-apex">
        <div className="max-w-3xl">
          <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> How we work</div>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
            A 5-step process that keeps things{" "}
            <span className="text-apex-orange">calm, clear, and effective.</span>
          </h2>
        </div>

        <div className="mt-16 relative">
          {/* connecting line */}
          <div aria-hidden className="hidden md:block absolute left-0 right-0 top-[86px] h-px">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="origin-left h-px bg-gradient-to-r from-apex-blue via-apex-cyan to-apex-orange"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-5 md:gap-4">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="relative"
                  data-testid={`process-step-${i}`}
                >
                  {/* dot on line */}
                  <div className="hidden md:flex justify-center mb-6">
                    <div className="relative w-4 h-4">
                      <span className="absolute inset-0 rounded-full bg-apex-orange" />
                      <span className="absolute inset-[-6px] rounded-full bg-apex-orange/25 blur-md animate-pulse" />
                    </div>
                  </div>

                  <div className="glass glass-hover p-6 h-full relative overflow-hidden">
                    <div className="flex items-center justify-between">
                      <span className="stroke-num text-4xl font-heading">{s.num}</span>
                      <span className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center">
                        <Icon className="w-4.5 h-4.5 text-apex-cyan" strokeWidth={1.6} />
                      </span>
                    </div>
                    <div className="mt-5 font-heading font-bold text-lg">{s.title}</div>
                    <p className="mt-2 text-sm text-white/65 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
